import { Link } from 'react-router-dom';
import {
    CardGrid,
    ContentSection,
    LinkRow,
    PageFrame,
    PageHero,
    ProofBlock,
    RoutingLogicDiagram,
    TrustBoundaryDiagram,
    TruthStateGrid,
} from '../components/PageBlocks';
import { CLAIM_TRUTH_CLASSES, getPageConfig } from '../data/page-config.mjs';

const page = getPageConfig('/security');

const SecurityPage = () => {
    return (
        <PageFrame>
            <PageHero
                sectionId="overview"
                variant="stacked"
                numeral="IV"
                eyebrow={page.routePurpose}
                title={page.h1}
                intro="AvenBox treats privacy as a system boundary, not a settings page. The public security story is about where trust sits, how routing is constrained, and what the product does not ask users to accept on faith."
                primaryCta={{ label: 'Join Waitlist', to: '/contact' }}
                secondaryCta={{ label: 'Contact', to: '/contact', variant: 'secondary' }}
                proofStrip={[
                    'Privacy by architecture.',
                    'Routing is part of the trust model.',
                    'Claims stop where proof stops.',
                ]}
            />

            <ContentSection
                sectionId="privacy-architecture"
                eyebrow="Privacy By Architecture"
                title="The default path should preserve trust, not spend it"
                intro="AvenBox treats privacy as a system boundary — not a toggle, not a policy, and not a promise. This section explains how the default architecture enforces trust rather than asking users to assume it."
                borderTop={false}
            >
                <TrustBoundaryDiagram
                    inside={[
                        { label: 'Local execution' },
                        { label: 'Private prompts' },
                        { label: 'Local AI backend' },
                        { label: 'Orchestration logic' },
                        { label: 'Continuity coordination' },
                    ]}
                    outside={[
                        { label: 'Trusted extension' },
                        { label: 'Controlled external paths' },
                    ]}
                    caption="The default boundary keeps work local. Extension is explicit, not automatic."
                />
                <CardGrid
                    pattern="meta"
                    items={[
                        {
                            title: 'Local path as the default posture',
                            body: 'Suitable work should stay local first. That keeps the default trust posture close to the user rather than outsourced by habit.',
                        },
                        {
                            title: 'Trusted extension instead of blind delegation',
                            body: "When work has to extend beyond the immediate local system, the architecture still favors environments under the user's control rather than invisible third-party dependence.",
                        },
                        {
                            title: 'External services as explicit choices',
                            body: 'If an outside service is used, that path should be explicit, bounded, and understandable rather than treated as a silent default assumption.',
                        },
                    ]}
                />
                <p className="text-body section-internal-link">
                    Full architecture detail on the <Link to="/technology">Technology page</Link>.
                </p>
            </ContentSection>

            <section className="ed-pullquote" aria-label="Security principle">
                <div className="ed-pullquote__inner">
                    <span className="ed-pullquote__mark" aria-hidden="true">&ldquo;</span>
                    <blockquote className="ed-pullquote__text">
                        <em>Privacy is a system boundary, not a settings page.</em> If the architecture does not enforce it, no policy ever will.
                    </blockquote>
                    <span className="ed-pullquote__attribution">Aven Studios — founding principle</span>
                </div>
            </section>

            <ContentSection
                sectionId="routing-logic"
                eyebrow="Routing Logic"
                title="Where trust flows — and where it stops by default"
                intro="Every task takes a path. The architecture makes those paths visible and explicit—the default path always preserves privacy first."
            >
                {/* Class 2 — Architecture: routing logic diagram showing 3 paths from control decision.
                    This is the most important structural visual on the Security page.
                    It shows that routing is a system decision, not a policy toggle.
                    Future upgrade: annotate with current implementation status per path. */}
                <RoutingLogicDiagram
                    caption="All three paths originate from the same control decision point. Local execution is the default. Extension and external access are explicit choices — never automatic."
                />
                <p className="text-body section-internal-link">
                    How the execution mode model works on the <Link to="/technology">Technology page</Link>.
                </p>
            </ContentSection>

            <ContentSection
                sectionId="trust-boundary"
                eyebrow="Trust Boundary"
                title="The security story starts with where trust sits"
                intro="A serious privacy product has to explain what remains inside the default boundary, what may extend outward, and what the user is expected to understand."
            >
                <CardGrid
                    pattern="meta"
                    items={[
                        {
                            title: 'Inside the default boundary',
                            body: 'The system assumes privacy-sensitive work deserves local control, visible routing, and the ability to keep context close unless there is a clear reason to move it.',
                        },
                        {
                            title: 'Explicit extension, not silent leakage',
                            body: 'Extension is part of the design, but it should happen through trusted paths the user can understand rather than through convenience logic that quietly spends trust.',
                        },
                        {
                            title: 'Visibility instead of assumption',
                            body: 'The architecture is trying to reduce accidental exposure, opaque model routing, and the loss of control that appears when intelligence is pushed outward by default.',
                        },
                    ]}
                />
            </ContentSection>

            <ContentSection
                sectionId="claim-discipline"
                eyebrow="Claim Discipline"
                title="What AvenBox is not claiming"
                intro="Credibility improves when the page limits interpretation instead of stretching it."
            >
                <CardGrid
                    pattern="meta"
                    items={[
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.REAL_NOW,
                            title: 'No finished-certification theater',
                            body: 'AvenBox does not claim completed enterprise certifications, regulatory approvals, or audit programs. None have been publicly documented, and none will be claimed until they are.',
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
                            title: 'No claim of total completion',
                            body: 'The current privacy posture can be described as a serious prototype direction with real architectural logic, not as a finished product where every control layer is already locked down.',
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.ROADMAP,
                            title: 'No future controls presented as current reality',
                            body: 'Later hardening, broader enterprise controls, and deeper ecosystem trust layers are part of the roadmap — not part of what the product delivers today.',
                        },
                    ]}
                />
            </ContentSection>

            <ContentSection
                sectionId="truth-state"
                eyebrow="Truth State"
                title="Current public reality"
            >
                {/* Class 3 — Prototype Proof: current privacy posture evidence.
                    This block shows what the privacy model looks like today — honestly.
                    Expand with real screenshots or system evidence when available.
                    Do not add aspirational claims here. This is current-state only. */}
                <ProofBlock
                    title="Current privacy posture"
                    truthClass={CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW}
                    evidence={
                        <ul className="surface-card__list">
                            <li className="text-body">Local-first routing logic enforced at the architecture level — not by user settings</li>
                            <li className="text-body">Trust boundary separation between local execution and extension paths</li>
                            <li className="text-body">Explicit routing for external service access — no silent cloud delegation by default</li>
                            <li className="text-body">Privacy behavior is baked into the system model, not applied after the fact</li>
                        </ul>
                    }
                    caption="Current prototype direction. Not a finished security product. Privacy hardening and broader controls are ongoing product work. This block will be updated as posture matures."
                />
                <TruthStateGrid
                    items={[
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.REAL_NOW,
                            title: 'Privacy-by-architecture thesis',
                            description:
                                'The real present-tense layer is the trust-boundary model itself: local-first routing, explicit extension logic, and privacy treated as system behavior.',
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
                            title: 'Current privacy posture',
                            description:
                                'The current product behavior around continuity, routing controls, and privacy-facing experience is best treated as a serious prototype path still being refined.',
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.ROADMAP,
                            title: 'Later hardening and scale',
                            description:
                                'Broader security maturity, later enterprise readiness, and deeper control layers remain roadmap work rather than present-tense proof.',
                        },
                    ]}
                />
                <p className="text-body section-internal-link">
                    Architecture details on the <Link to="/technology">Technology page</Link>. Questions on <Link to="/faq">FAQ</Link>.
                </p>
            </ContentSection>

            <ContentSection
                sectionId="inquiry"
                eyebrow="Serious Inquiry"
                title="Trust-sensitive questions deserve direct answers"
            >
                <LinkRow
                    links={[
                        { label: 'Technology', to: '/technology' },
                        { label: 'Pricing', to: '/pricing' },
                        { label: 'FAQ', to: '/faq' },
                        { label: 'Contact', to: '/contact' },
                    ]}
                />
            </ContentSection>
        </PageFrame>
    );
};

export default SecurityPage;
